<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import rudyAvatar from 'dashboard/assets/images/rudy-avatar.png';
import OnboardingStepLayout from './OnboardingStepLayout.vue';
import { useRegisterOnboardingNav } from '../useOnboardingNav';

const store = useStore();
const { t } = useI18n();
const fileInput = ref(null);

const uiFlags = computed(() => store.state.onboarding.uiFlags);
const error = computed(() => store.state.onboarding.error);

const assistantName = computed({
  get: () =>
    store.state.onboarding.assistantName ||
    store.getters['onboarding/suggestedAssistantName'],
  set: value => store.dispatch('onboarding/setAssistantName', value),
});

const avatarPreview = computed(
  () => store.state.onboarding.assistantAvatar || rudyAvatar
);

const hasCustomAvatar = computed(() =>
  Boolean(store.state.onboarding.assistantAvatar)
);

function openFilePicker() {
  fileInput.value?.click();
}

function onFileSelected(event) {
  const [file] = event.target.files || [];
  if (!file) return;

  const validTypes = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/gif',
    'image/webp',
  ];
  if (!validTypes.includes(file.type)) {
    store.commit(
      'onboarding/SET_ERROR',
      t('ONBOARDING_WIZARD.ASSISTANT.INVALID_TYPE')
    );
    event.target.value = null;
    return;
  }

  const maxSize = 2 * 1024 * 1024;
  if (file.size > maxSize) {
    store.commit(
      'onboarding/SET_ERROR',
      t('ONBOARDING_WIZARD.ASSISTANT.TOO_LARGE')
    );
    event.target.value = null;
    return;
  }

  const reader = new FileReader();
  reader.onload = () => {
    store.dispatch('onboarding/setAssistantAvatar', reader.result);
    store.commit('onboarding/SET_ERROR', null);
  };
  reader.readAsDataURL(file);
  event.target.value = null;
}

function clearAvatar() {
  store.dispatch('onboarding/setAssistantAvatar', '');
}

const continueSetup = () => store.dispatch('onboarding/saveAssistant');

const skipAssistant = () => store.dispatch('onboarding/skipAssistant');

useRegisterOnboardingNav(() => ({
  secondary: {
    label: t('ONBOARDING_WIZARD.SKIP'),
    loading: uiFlags.value.isProvisioning,
    onClick: skipAssistant,
  },
  primary: {
    label: t('ONBOARDING_WIZARD.CONTINUE'),
    loading: uiFlags.value.isSaving,
    onClick: continueSetup,
  },
}));

const inputClass =
  'w-full h-12 px-4 text-base rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-woot-500 !mb-0';
</script>

<template>
  <OnboardingStepLayout
    :step-label="t('ONBOARDING_WIZARD.OVERVIEW.PHASES.starting_with_ai.TITLE')"
    :title="t('ONBOARDING_WIZARD.ASSISTANT.TITLE')"
    :description="t('ONBOARDING_WIZARD.ASSISTANT.DESCRIPTION')"
  >
    <div class="w-full flex flex-col items-start gap-5">
      <div class="relative">
        <button
          type="button"
          class="relative group rounded-full overflow-hidden size-28 focus:outline-none focus:ring-2 focus:ring-woot-500"
          :aria-label="t('ONBOARDING_WIZARD.ASSISTANT.UPLOAD')"
          @click="openFilePicker"
        >
          <img
            :src="avatarPreview"
            alt=""
            class="size-full"
            :class="hasCustomAvatar ? 'object-cover' : 'object-contain'"
          />
          <span
            class="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-slate-900/50 opacity-0 group-hover:opacity-100 transition-opacity text-white rounded-full"
          >
            <Icon icon="i-lucide-camera" class="size-5" />
            <span class="text-xs font-medium px-2 text-center">
              {{ t('ONBOARDING_WIZARD.ASSISTANT.UPLOAD') }}
            </span>
          </span>
        </button>
        <button
          v-if="hasCustomAvatar"
          type="button"
          class="absolute -top-1 -right-1 size-7 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-center text-slate-500 hover:text-red-500"
          :aria-label="t('ONBOARDING_WIZARD.ASSISTANT.REMOVE_PHOTO')"
          @click="clearAvatar"
        >
          <Icon icon="i-lucide-x" class="size-3.5" />
        </button>
        <input
          ref="fileInput"
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
          class="hidden"
          @change="onFileSelected"
        />
      </div>

      <p class="text-sm text-slate-500 dark:text-slate-400 m-0">
        {{ t('ONBOARDING_WIZARD.ASSISTANT.PHOTO_HINT') }}
      </p>

      <div class="w-full flex flex-col gap-2">
        <label class="text-sm font-semibold text-slate-700 dark:text-slate-200">
          {{ t('ONBOARDING_WIZARD.ASSISTANT.NAME_LABEL') }}
        </label>
        <input
          v-model="assistantName"
          type="text"
          required
          :class="inputClass"
          :placeholder="t('ONBOARDING_WIZARD.ASSISTANT.NAME_PLACEHOLDER')"
        />
        <p v-if="error" class="text-sm text-red-600 dark:text-red-400 m-0">
          {{ error }}
        </p>
      </div>
    </div>
  </OnboardingStepLayout>
</template>
